import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ApolloClient, InMemoryCache, ApolloProvider, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import Header from './components/Header';
import Home from './pages/Home';
import CandidateSearch from './pages/CandidateSearch';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Help from './pages/Help';
import MyConnections from './pages/MyConnections';
import Profile from './pages/Profile';
import Careers from './pages/careers/Careers';
import CareerDetail from './pages/careers/JobDetail';
import CareerApplication from './pages/careers/ApplicationForm';
import About from './pages/About';
import Vision from './pages/Vision';
import Feed from './feed/pages/Feed';
import SinglePost from './feed/pages/SinglePost';
import Notifications from './feed/pages/Notifications';
import Contact from './pages/Contact';
import MVPDevelopment from './pages/MVPDevelopment';
import StartupJobs from './pages/StartupJobs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import Onboarding from './pages/Onboarding';
import EmailVerification from './pages/EmailVerification';
import Pricing from './pages/Pricing';
import Payment from './pages/Payment';
import PostJobWizard from './pages/post-job/PostJobWizard';
import PostJobSuccess from './pages/post-job/PostJobSuccess';
import PostJobStatus from './pages/post-job/PostJobStatus';
import CryptoPay from './pages/post-job/CryptoPay';
import Init from './pages/Init';
import AdminDashboard from './pages/admin/AdminDashboard';
import ScrollReveal from './components/ScrollReveal';
import PageEnter from './components/PageEnter';
import useAuthStore, { setApolloClient, isTokenExpired } from './stores/authStore';
import useAdminAuthStore from './stores/adminAuthStore';
import AdminSignIn from './pages/AdminSignIn';
import { trackPageVisit } from './services/telegramTracker';
import './App.css';
import './MainContentTheme.css';

const AdminRoute = ({ children }) => {
  const hasHydrated = useAdminAuthStore((state) => state._hasHydrated);
  const token = useAdminAuthStore((state) => state.token);
  const isAdmin = useAdminAuthStore((state) => state.isAdmin);
  const isLoggedIn = !!token && !isTokenExpired(token);

  // Wait for persisted admin auth to load — otherwise a flash of null auth redirects away
  if (!hasHydrated) {
    return <Init />;
  }

  if (!isLoggedIn) {
    return (
      <Navigate
        to={`/admin-signin?next=${encodeURIComponent('/admin-cofounder-hubs')}`}
        replace
      />
    );
  }

  if (!isAdmin()) {
    return (
      <Navigate
        to={`/admin-signin?next=${encodeURIComponent('/admin-cofounder-hubs')}`}
        replace
      />
    );
  }

  return children;
};

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_GRAPHQL_URI || 'http://localhost:8000/graphql',
});

// Add auth token and Turnstile token to requests
const authLink = setContext((_, { headers }) => {
  const token = useAuthStore.getState().token;
  const turnstileToken = sessionStorage.getItem('turnstile-token');
  
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
      'cf-turnstile-token': turnstileToken || "",
    }
  }
});

// Handle authentication and CAPTCHA errors
const errorLink = onError(({ graphQLErrors, networkError }) => {
  const turnstileEnabled = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;
  
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, extensions }) => {
      if (extensions?.code === 'UNAUTHENTICATED' || message.includes('authentication')) {
        // Clear auth state and redirect to signin
        useAuthStore.getState().signout();
        window.location.href = '/signin';
      }
      if (turnstileEnabled && message.includes('CAPTCHA')) {
        // Clear Turnstile token and reload to show CAPTCHA again
        sessionStorage.removeItem('turnstile-token');
        window.location.reload();
      }
    });
  }
  if (networkError) {
    if (networkError.statusCode === 401) {
      // Token expired or invalid - sign out user
      useAuthStore.getState().signout();
      window.location.href = '/signin';
    }
    if (turnstileEnabled && networkError.statusCode === 403) {
      // CAPTCHA verification failed - clear token and reload
      sessionStorage.removeItem('turnstile-token');
      window.location.reload();
    }
  }
});

const client = new ApolloClient({
  link: errorLink.concat(authLink.concat(httpLink)),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all'
    },
    query: {
      errorPolicy: 'all'
    }
  }
});

// Register Apollo client with auth store for cache clearing on signout
setApolloClient(client);

// Global error handler to suppress MetaMask network errors
window.addEventListener('error', (event) => {
  if (event.message && event.message.includes('Failed to connect to MetaMask')) {
    event.preventDefault();
    return false;
  }
}, true);

// Suppress unhandled promise rejections from MetaMask
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && event.reason.message && event.reason.message.includes('Failed to connect')) {
    event.preventDefault();
    return false;
  }
}, true);

function RouteTracker() {
  const location = useLocation();
  const { user } = useAuthStore();

  useEffect(() => {
    trackPageVisit(location.pathname, user?.username || null);
  }, [location.pathname, user?.username]);

  return null;
}

function App() {
  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('themeMode');
    return savedTheme ? JSON.parse(savedTheme) : false;
  });

  const [vpnChecked, setVpnChecked] = useState(false);

  useEffect(() => {
    localStorage.setItem('themeMode', JSON.stringify(darkMode));
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  // Schedule token refresh on app load if user is authenticated
  useEffect(() => {
    const token = useAuthStore.getState().token;
    if (token) {
      // Token already expired (e.g. user came back after hours) — sign out immediately
      if (isTokenExpired(token)) {
        useAuthStore.getState().signout();
      } else {
        useAuthStore.getState().scheduleTokenRefresh();
      }
    }
  }, []);

  // VPN check on app load
  useEffect(() => {
    const checkVPN = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/health`, {
          method: 'HEAD',
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        
        if (response.status === 403) {
          setVpnChecked(false);
        } else {
          // No VPN detected, allow access
          setVpnChecked(true);
        }
      } catch (error) {
        console.error('VPN check error:', error);
        // On error, allow access
        setVpnChecked(true);
      }
    };

    checkVPN();
  }, []);

  // Show Init page while VPN check is in progress
  if (!vpnChecked) {
    return <Init />;
  }

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <ApolloProvider client={client}>
        <Router>
          <RouteTracker />
          <ScrollReveal />
          <div className="App">
          <Routes>
            <Route path="/signin" element={<PageEnter><SignIn /></PageEnter>} />
            <Route path="/admin-signin" element={<PageEnter><AdminSignIn /></PageEnter>} />
            <Route path="/signup" element={<PageEnter><SignUp /></PageEnter>} />
            <Route path="/verify-email" element={<PageEnter><EmailVerification /></PageEnter>} />
            <Route path="/onboarding" element={<PageEnter><Onboarding /></PageEnter>} />
            <Route path="/forgot-password" element={<PageEnter><ForgotPassword /></PageEnter>} />
            <Route path="/reset-password/:token" element={<PageEnter><ResetPassword /></PageEnter>} />
            <Route
              path="/admin-cofounder-hubs"
              element={
                <AdminRoute>
                  <PageEnter><AdminDashboard /></PageEnter>
                </AdminRoute>
              }
            />
            <Route
              path="/admin"
              element={<Navigate to="/admin-cofounder-hubs" replace />}
            />
            <Route
              path="/admin-cofouder-hubs"
              element={<Navigate to="/admin-cofounder-hubs" replace />}
            />
            <Route
              path="/*"
              element={
                <>
                  <div className={darkMode ? 'dark' : 'light'}>
                    <Header darkMode={darkMode} setDarkMode={setDarkMode} />
                  </div>
                  <main className="main-content">
                    <PageEnter>
                      <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/search" element={<CandidateSearch />} />
                      <Route path="/connections" element={<MyConnections />} />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/feed" element={<Feed />} />
                      <Route path="/feed/:postId" element={<SinglePost />} />
                      <Route path="/notifications" element={<Notifications />} />
                      <Route path="/help" element={<Help />} />
                      <Route path="/careers" element={<Careers />} />
                      <Route path="/careers/:jobId" element={<CareerDetail />} />
                      <Route path="/careers/:jobId/apply" element={<CareerApplication />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/vision" element={<Vision />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/mvp-development" element={<MVPDevelopment />} />
                      <Route path="/startup-jobs" element={<StartupJobs />} />
                      <Route path="/post-job" element={<PostJobWizard />} />
                      <Route path="/post-job/success" element={<PostJobSuccess />} />
                      <Route path="/post-job/status" element={<PostJobStatus />} />
                      <Route path="/post-job/crypto-pay" element={<CryptoPay />} />
                      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                      <Route path="/terms-of-service" element={<TermsOfService />} />
                      <Route path="/cookie-policy" element={<CookiePolicy />} />
                      <Route path="/pricing" element={<Pricing />} />
                      <Route path="/payment" element={<Payment />} />
                      </Routes>
                    </PageEnter>
                  </main>
                </>
              }
            />
          </Routes>
        </div>
      </Router>
    </ApolloProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
